// Staff/coach-facing result-entries card on the athlete detail page (Wave 2:
// swimming/gymnastics/rowing/golf/diving/beach_volleyball). Read-only display
// grouped by event (beach is flat); verified rows show a blue seal. Staff /
// partners (canVerify) get a toggle seal, coaches see it read-only — same rule
// as EventMarksCard. Rendered only for result_entries athletes.
import React from 'react';
import SealButton from './SealButton';
import useResultEntryVerify from './useResultEntryVerify';
import { contextLabel } from '../../lib/resultEntries';

function groupByEvent(entries, positions, isFlat) {
  if (isFlat) return [{ key: 'pairs', label: '', rows: entries }];
  const order = new Map(positions.map((p, i) => [p.slug, i]));
  const label = new Map(positions.map(p => [p.slug, p.label]));
  const byEvent = new Map();
  for (const e of entries) {
    if (!byEvent.has(e.event_key)) byEvent.set(e.event_key, []);
    byEvent.get(e.event_key).push(e);
  }
  return [...byEvent.entries()]
    .sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999))
    .map(([key, rows]) => ({ key, label: label.get(key) || key, rows }));
}

export default function ResultEntriesCard({ userId, entries, setEntries, canVerify, apiUrl, getConfig, positions, sportSlug }) {
  const { toggleVerify, busyId } = useResultEntryVerify({ userId, apiUrl, getConfig, setEntries });
  if (!entries || entries.length === 0) return null;

  const isFlat = sportSlug === 'beach_volleyball';
  const groups = groupByEvent(entries, positions, isFlat);

  return (
    <div className="ae-card ae-card--pad">
      <div className="ae-card__head">
        <h3 className="ae-card__title">Results</h3>
        {canVerify && <span className="ae-card__hint">Click a seal to verify</span>}
      </div>
      <div className="ae-marks">
        {groups.map(g => (
          <div key={g.key} className="ae-mark-group">
            {g.label && <div className="ae-mark-group__head">{g.label}</div>}
            {g.rows.map(e => {
              const ctx = contextLabel(e, sportSlug);
              return (
                <div key={e.id} className="ae-accolade">
                  <div className="ae-accolade__body">
                    <div className="ae-accolade__title">
                      {e.mark_value}
                      {e.is_pr && <span className="ae-mark-badge ae-mark-badge--pr">PR</span>}
                      {e.is_sb && <span className="ae-mark-badge ae-mark-badge--sb">SB</span>}
                    </div>
                    <div className="ae-accolade__meta">
                      {e.meet_name && <span className="ae-accolade__src">{e.meet_name}</span>}
                      {e.meet_date && <span className="ae-accolade__src">{e.meet_date}</span>}
                      {ctx && <span className="ae-accolade__src">{ctx}</span>}
                      {e.season_year && <span className="ae-accolade__src">{e.season_year}</span>}
                    </div>
                  </div>
                  {canVerify ? (
                    <SealButton
                      className="ae-accolade__seal"
                      state={e.verified ? 'verified' : 'unverified'}
                      title={e.verified ? 'Verified — click to unverify' : 'Not verified — click to verify'}
                      disabled={busyId === e.id}
                      onToggle={() => toggleVerify(e)}
                    />
                  ) : (
                    e.verified && (
                      <SealButton className="ae-accolade__seal" state="verified" title="Verified" disabled onToggle={() => {}} />
                    )
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
